import { DESIGN } from '../constants';
import type { EditorState, LayerBox, LayerId, TextLayer } from '../types';
import { withAlpha } from './color';

export interface RenderSource {
  image: CanvasImageSource | null;
  imageWidth: number;
  imageHeight: number;
}

export interface RenderOptions extends RenderSource {
  state: EditorState;
  /** Target size in real pixels. Everything is drawn at `width / designWidth`. */
  width: number;
  height: number;
  /** Preview-only affordance; never drawn on an export. */
  selection?: LayerId | null;
}

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function roundRectPath(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, radius);
    return;
  }
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function supportsFilter(ctx: Ctx2D): boolean {
  return 'filter' in ctx;
}

function buildFilter(state: EditorState, scale: number): string {
  const { brightness, contrast, saturate, blur } = state.filters;
  const parts: string[] = [];
  if (brightness !== 100) parts.push(`brightness(${brightness}%)`);
  if (contrast !== 100) parts.push(`contrast(${contrast}%)`);
  if (saturate !== 100) parts.push(`saturate(${saturate}%)`);
  // Canvas blur radius is measured in real device pixels regardless of the
  // transform, so it is scaled by hand to stay proportional across export sizes.
  if (blur > 0) parts.push(`blur(${blur * scale}px)`);
  return parts.length > 0 ? parts.join(' ') : 'none';
}

/** Cover-fit, with bleed so a blur filter never samples the transparent edge. */
function drawCover(
  ctx: Ctx2D,
  source: RenderSource,
  width: number,
  height: number,
  bleed: number,
): void {
  const { image, imageWidth, imageHeight } = source;
  if (!image || imageWidth <= 0 || imageHeight <= 0) return;
  const boxW = width + bleed * 2;
  const boxH = height + bleed * 2;
  const factor = Math.max(boxW / imageWidth, boxH / imageHeight);
  const w = imageWidth * factor;
  const h = imageHeight * factor;
  ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
}

interface LayerLayout {
  lines: string[];
  boxX: number;
  boxY: number;
  boxW: number;
  boxH: number;
  padding: number;
  lineHeight: number;
  fontSize: number;
}

function layoutLayer(
  ctx: Ctx2D,
  layer: TextLayer,
  width: number,
  height: number,
  scale: number,
): LayerLayout | null {
  const text = layer.text;
  if (text.trim().length === 0) return null;

  const fontSize = layer.fontSize * scale;
  ctx.font = `${layer.fontWeight} ${fontSize}px "${layer.fontFamily}", sans-serif`;
  if ('letterSpacing' in ctx) {
    ctx.letterSpacing = `${layer.letterSpacing * scale}px`;
  }

  const lines = text.split('\n');
  const widest = lines.reduce((max, line) => Math.max(max, ctx.measureText(line).width), 0);
  const lineHeight = fontSize * layer.lineHeight;
  const padding = layer.backgroundPadding * scale;
  const boxW = widest + padding * 2;
  const boxH = lineHeight * lines.length + padding * 2;
  const centreX = width / 2 + layer.x * scale;
  const centreY = height / 2 + layer.y * scale;

  return {
    lines,
    boxX: centreX - boxW / 2,
    boxY: centreY - boxH / 2,
    boxW,
    boxH,
    padding,
    lineHeight,
    fontSize,
  };
}

function drawLayer(ctx: Ctx2D, layer: TextLayer, layout: LayerLayout, scale: number): void {
  const { lines, boxX, boxY, boxW, boxH, padding, lineHeight } = layout;

  if (layer.backgroundEnabled && layer.backgroundOpacity > 0) {
    ctx.save();
    ctx.fillStyle = withAlpha(layer.backgroundColor, layer.backgroundOpacity);
    roundRectPath(ctx, boxX, boxY, boxW, boxH, layer.backgroundRadius * scale);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  ctx.textBaseline = 'middle';
  ctx.textAlign = layer.align === 'center' ? 'center' : layer.align === 'right' ? 'right' : 'left';
  const textX =
    layer.align === 'center'
      ? boxX + boxW / 2
      : layer.align === 'right'
        ? boxX + boxW - padding
        : boxX + padding;

  const applyShadow = () => {
    if (!layer.shadowEnabled) return;
    ctx.shadowColor = layer.shadowColor;
    ctx.shadowBlur = layer.shadowBlur * scale;
    ctx.shadowOffsetY = layer.shadowBlur * scale * 0.15;
  };
  const clearShadow = () => {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
  };

  lines.forEach((line, index) => {
    const y = boxY + padding + lineHeight * (index + 0.5);

    if (layer.strokeEnabled && layer.strokeWidth > 0) {
      // Stroke first, at double width, so the fill masks the inner half and the
      // outline reads as an outside stroke like `-webkit-text-stroke`.
      applyShadow();
      ctx.lineJoin = 'round';
      ctx.miterLimit = 2;
      ctx.lineWidth = layer.strokeWidth * 2 * scale;
      ctx.strokeStyle = layer.strokeColor;
      ctx.strokeText(line, textX, y);
      clearShadow();
    } else {
      applyShadow();
    }

    ctx.fillStyle = layer.color;
    ctx.fillText(line, textX, y);
    clearShadow();
  });

  ctx.restore();
}

function drawSelection(ctx: Ctx2D, layout: LayerLayout, scale: number): void {
  ctx.save();
  ctx.setLineDash([8 * scale, 6 * scale]);
  ctx.lineWidth = Math.max(1, 2 * scale);
  ctx.strokeStyle = '#a259c6';
  roundRectPath(ctx, layout.boxX, layout.boxY, layout.boxW, layout.boxH, 6 * scale);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draws the whole thumbnail and returns each text layer's hit box in design
 * space. The preview and every export size call this same function, so what the
 * user drags is exactly what lands in the downloaded file.
 */
export function renderThumbnail(ctx: Ctx2D, options: RenderOptions): LayerBox[] {
  const { state, width, height, selection } = options;
  const design = DESIGN[state.ratio];
  const scale = width / design.width;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const frameRadius = state.frame.enabled ? state.frame.radius * scale : 0;

  ctx.save();
  roundRectPath(ctx, 0, 0, width, height, frameRadius);
  ctx.clip();

  ctx.fillStyle = state.backgroundColor;
  ctx.fillRect(0, 0, width, height);

  if (options.image) {
    const filter = buildFilter(state, scale);
    const canFilter = supportsFilter(ctx);
    ctx.save();
    if (canFilter) ctx.filter = filter;
    drawCover(ctx, options, width, height, state.filters.blur * scale * 3);
    ctx.restore();
  }

  if (state.filters.overlayOpacity > 0) {
    ctx.save();
    ctx.fillStyle = withAlpha(state.filters.overlayColor, state.filters.overlayOpacity);
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  const boxes: LayerBox[] = [];
  const order: LayerId[] = ['main', 'sub'];
  for (const id of order) {
    const layer = state[id];
    const layout = layoutLayer(ctx, layer, width, height, scale);
    if (!layout) continue;
    drawLayer(ctx, layer, layout, scale);
    if (selection === id) drawSelection(ctx, layout, scale);
    boxes.push({
      id,
      x: layout.boxX / scale,
      y: layout.boxY / scale,
      width: layout.boxW / scale,
      height: layout.boxH / scale,
    });
  }

  ctx.restore();

  if (state.frame.enabled && state.frame.width > 0) {
    const stroke = state.frame.width * scale;
    ctx.save();
    ctx.lineWidth = stroke;
    ctx.strokeStyle = state.frame.color;
    roundRectPath(
      ctx,
      stroke / 2,
      stroke / 2,
      width - stroke,
      height - stroke,
      Math.max(0, frameRadius - stroke / 2),
    );
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();
  return boxes;
}

/** Topmost layer whose hit box contains the point (design-space coordinates). */
export function hitTest(boxes: LayerBox[], x: number, y: number): LayerId | null {
  for (let i = boxes.length - 1; i >= 0; i -= 1) {
    const box = boxes[i]!;
    if (x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height) {
      return box.id;
    }
  }
  return null;
}
